import { vec3 } from 'gl-matrix';
import { PointMm } from '~/domain/geometry/patientGeometry';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D, imageIntensityPixels } from '~/domain/patient/image3d';
import { Grid } from '~/engine/data/common/Grid';

/** Fewer surviving samples than this is not a profile: it cannot be plotted. */
const MIN_PROFILE_SAMPLES = 2;

/**
 * A line produced too few usable samples to be a profile — it missed the grid
 * entirely, or only clipped it. Carries the count, and is named so a caller
 * can report the reason from `name` alone, which is what keeps the log free of
 * the description and UID.
 *
 * Deliberately NOT a {@link DomainError}, despite living in `domain/`. That
 * base is a behavioural marker, not a convenience: every consumer of it
 * (`useRoiCommands`, `useStructureSetCommands`, `useDeleteDataset`) swallows
 * the error into a snackbar and returns. This one is caught inside the
 * comparison and never reaches the user, so it must not join that category.
 */
export class InsufficientSamplesError extends RangeError {
    public readonly sampleCount: number;

    public constructor(sampleCount: number) {
        super(`${sampleCount} usable sample(s), need ${MIN_PROFILE_SAMPLES}`);
        this.name = 'InsufficientSamplesError';
        this.sampleCount = sampleCount;
    }
}

/** One sampled value at a distance along a line. */
export interface DistanceSample {
    /** Distance from the line's start point, in mm. */
    readonly distanceMm: number;
    /** The value at that point, in the dataset's native units. */
    readonly value: number;
}

/**
 * Resample `sampleAt(t)`, `t ∈ [0, 1]`, over `lengthMm` at no coarser than
 * `maxStepMm`. `sampleAt` returns the native value at `t`, or null when the
 * point is off-grid; null and non-finite samples are dropped. Both endpoints
 * are always sampled; the segment count is ceil'd so the actual spacing never
 * exceeds `maxStepMm`. This bounds the spacing, it does not visit every voxel
 * a line crosses: an oblique line reads a subset of them. Sampling is by
 * distance because the plot's x-axis is distance and every dataset must be
 * sampled at the same positions to be overlaid.
 *
 * @throws RangeError for a non-positive length or step. A line with no extent
 *   has nothing to plot: the tool refuses to place one, so reaching here means
 *   a caller built an invalid line.
 *
 * Returns whatever survived, including nothing: how many samples a caller
 * needs is the caller's rule, not this function's. {@link sampleGridAlongLine}
 * applies the line-profile one.
 */
export function resampleAtSpacing(
    lengthMm: number,
    maxStepMm: number,
    sampleAt: (t: number) => number | null
): DistanceSample[] {
    // Negated comparisons so a NaN (a degenerate grid) is rejected too.
    if (!(lengthMm > 0) || !(maxStepMm > 0)) {
        throw new RangeError(
            `resampleAtSpacing: lengthMm and maxStepMm must be positive, got ${lengthMm} and ${maxStepMm}`
        );
    }
    const samples: DistanceSample[] = [];
    const segments = Math.ceil(lengthMm / maxStepMm);
    for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const value = sampleAt(t);
        if (value !== null && Number.isFinite(value)) {
            samples.push({ distanceMm: t * lengthMm, value });
        }
    }
    return samples;
}

/**
 * Sample native intensity along a patient-space line through a voxel grid (no
 * view, no WebGL), so non-displayed datasets can be profiled. Step = finest
 * voxel dimension; the index accessor avoids requiring a whole-volume array
 * for a line that touches only O(length) voxels.
 *
 * @throws RangeError for a grid with no positive resolution — a defective
 *   dataset, surfaced by the caller rather than sampled at a substitute step.
 * @throws InsufficientSamplesError when fewer than two samples survive. Refused
 *   rather than returned: one point is not a profile, and an empty array reads
 *   at the call site exactly like a profile that happens to be flat. A caller
 *   comparing several datasets catches this and omits the one it could not
 *   sample.
 */
function sampleGridAlongLine(
    grid: Grid,
    valueAtIndex: (index: number) => number,
    startMm: PointMm,
    endMm: PointMm,
    /**
     * Whether the voxel at this index holds retrieved data. A false answer
     * drops the sample, exactly as an off-grid point is dropped, so the curve
     * breaks over the gap rather than tracing the zero the buffer holds there.
     * Omitted where every voxel is always readable.
     */
    isReadableAtIndex?: (index: number) => boolean
): DistanceSample[] {
    const res = grid.getRes();
    const minRes = Math.min(
        Math.abs(res[0]),
        Math.abs(res[1]),
        Math.abs(res[2])
    );
    const sampleAt = (t: number): number | null => {
        const point = vec3.lerp(vec3.create(), startMm, endMm, t);
        const index = grid.flatIndexAt(point);
        if (index === null) {
            return null;
        }
        if (isReadableAtIndex !== undefined && !isReadableAtIndex(index)) {
            return null;
        }
        return valueAtIndex(index);
    };
    const samples = resampleAtSpacing(
        vec3.distance(startMm, endMm),
        minRes,
        sampleAt
    );
    if (samples.length < MIN_PROFILE_SAMPLES) {
        throw new InsufficientSamplesError(samples.length);
    }
    return samples;
}

/**
 * Profile a (possibly non-displayed) image along a patient-space line, read
 * via {@link imageIntensityPixels} so no full Float32 volume is allocated.
 *
 * A volume that is still filling is read from its Float32 buffer instead. That
 * buffer already exists for such a volume, so nothing extra is allocated, and
 * it is the one the arrival record describes; the Int16 accessor would refuse
 * the whole profile rather than the points that are actually absent, which
 * would drop the curve for a line lying entirely on slices that have arrived.
 * Points on rows that have not arrived are dropped, so the curve breaks over
 * them rather than tracing the zero the buffer holds — on a CT that zero is
 * water, and a profile is a measurement.
 *
 * @throws RangeError for a defective grid, and {@link InsufficientSamplesError}
 *   when the line misses this image, only clips it, or crosses too few
 *   arrived slices to make a profile.
 */
export function sampleImage3DAlongLine(
    image: Image3D,
    startMm: PointMm,
    endMm: PointMm
): DistanceSample[] {
    const pixels = image.isComplete
        ? imageIntensityPixels(image)
        : image.floatPixelData;
    const sliceVoxelCount = image.grid.sizeX * image.grid.sizeY;
    return sampleGridAlongLine(
        image.grid,
        (index) => pixels[index],
        startMm,
        endMm,
        (index) => image.hasArrivedAtZ(Math.floor(index / sliceVoxelCount))
    );
}

/**
 * Profile a dose along a patient-space line: true Gy via
 * {@link DoseMatrix.toDoseGy} per voxel, never the display-scaled dose and
 * never the whole-volume `realPixels` copy.
 *
 * @throws RangeError for a defective grid, and {@link InsufficientSamplesError}
 *   when the line misses this dose grid or only clips it.
 */
export function sampleDoseAlongLine(
    dose: DoseMatrix,
    startMm: PointMm,
    endMm: PointMm
): DistanceSample[] {
    // Reads the stored values and converts per sample, rather than taking the
    // whole scaled volume: a profile touches a few hundred voxels, and
    // materialising 45 MB of Float32 to read them would be the larger cost.
    const stored = dose.pixelData;
    return sampleGridAlongLine(
        dose.grid,
        (index) => dose.toDoseGy(stored[index]),
        startMm,
        endMm
    );
}
